import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding,
  Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges, ViewChild
} from '@angular/core';

import { Observable, Subscription } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { SdTitleComponent } from '../title/title.component';
import { BrandThemesEnum, ThemesEnum } from '../../_enums/themes.enum';
import { IconSizeEnum } from '../../_enums/icon.enum';
import { IHeading } from '../../_interfaces/heading.interface';
import { PositionEnum } from '../../_enums/position.enum';
import { SdBannerComponent } from '../medias/banner/banner.component';
import { SdIconComponent } from '../icons/icon/icon.component';
import { SdIsCollapsibleDirective } from '../../_directives/isCollapsible.directive';
import { SdThemeComponent } from '../abstract/theme/theme.component';
import { SizesEnum } from '../../_enums/sizes.enum';
import { ThemesTextEnum } from '../../_enums/themes-text.enum';
import { ThemesUtils } from '../../utils/themes.utils';
import { TypographyUtils } from '../../utils/typography.utils';
import { SdSkeletonComponent } from '../skeleton/skeleton.component';
import { SkeletonAppearanceEnum } from '../../_enums/skeleton.enum';
import { BrandsEnum } from '../../_enums/brands.enum';
import { SdHtmlComponent } from '../html/html.component';

@Component({
  selector: 'sd-heading',
  templateUrl: './heading.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdIsCollapsibleDirective,
    SdBannerComponent,
    SdTitleComponent,
    SdIconComponent,
    SdSkeletonComponent,
    TranslateModule,
    SdHtmlComponent
  ]
})
export class SdHeadingComponent extends SdThemeComponent implements AfterViewInit, OnDestroy, OnInit, OnChanges {
  @Input()
  isLoading = false;

  @Input()
  heading: IHeading;

  @Input()
  limit = 100;

  @Input()
  size: SizesEnum.xl | SizesEnum.l | SizesEnum.m | SizesEnum.s = SizesEnum.l;

  @Input()
  isUnderlined = false;

  @Input()
  isInteractive = false;

  @Input()
  brandCorner = BrandsEnum.default;

  /**
   * Allow to define the theme for the title is dark (default), light or "branded"
   * * @param {string} dark | light | brand
   */
  @Input()
  titleThemesConfig: ThemesTextEnum;

  /**
   * Allow to define if the text is aligned to center, to left Side of Left Under
   * * @param {string} center | leftSide | leftUnder
   */
  @Input()
  textConfig: PositionEnum.center | PositionEnum.left = PositionEnum.center;

  /**
   * Allow to define the theme for the content based on the parent's theme config
   * * @param {string} dark | light | promo-1 | promo-2 | stars | extra | brand
   */
  @Input()
  parentsThemesConfig: ThemesEnum | BrandThemesEnum = ThemesEnum.light;

  @Input()
  readMore = false;

  /**
   * Allow to define the level of the title tag (h1 - h6).
   * * @param {number} 1 to 6
   */
  @Input()
  titleLevel = 4;

  @Input()
  subtitleLevel?: number;

  /**
   * Icon size
   * * @param {string} icon size small || medium || large || logo;
   */
  @Input()
  iconSize: IconSizeEnum = IconSizeEnum.small;

  /**
   * Icon name of the icon for the opened state
   * * @param {string} icon name
   */
  @Input()
  iconNameOpened = 'icon-arrow-up';

  /**
   * Icon name of the icon for the closed state
   * * @param {string} icon name
   */
  @Input()
  iconNameClosed = 'icon-arrow-down';

  @Input()
  idReadMore: string;

  @Input()
  customClass = '';

  @Input()
  events: Observable<boolean>;

  @Input()
  isA11y = false;

  @Input()
  isDescription = false;

  @ViewChild('action')
  actionEl: ElementRef;

  @ViewChild('text')
  descriptionText: ElementRef;

  @Output()
  noData = new EventEmitter<boolean>();

  @Input() class = ''; // override the standard class attr with a new one.

  @Input()
  inverted = false;

  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.readMore ? 'is-read-more' : '',
      this.isUnderlined ? 'is-underlined' : '',
      this.size ? 'is-size-' + this.size : '',
      this.parentsThemesConfig && !this.titleThemesConfig
        ? 'has-parent-' + this.parentsThemesConfig
        : '',
      (this.contentThemesConfig && this.parentsThemesConfig) ||
      this.titleThemesConfig
        ? 'is-theme-' + this.contentThemesConfig
        : '',
      this.textConfig ? 'is-align-' + this.textConfig : '',
      this.customClass ? this.customClass : '',
      this.heading?.brandLogo ? 'has-brand-logo' : '',
      this.inverted ? 'is-inverted' : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  PositionEnum = PositionEnum;
  isOpen = false;
  sizesEnum = SizesEnum;
  iconSizeEnum = IconSizeEnum;
  skeletonAppearanceEnum = SkeletonAppearanceEnum;
  eventsSubscription: Subscription;
  descriptionLinks: HTMLElement[];
  getHTMLText = TypographyUtils.getHTMLText;
  manageThemesTextConfigUtils = ThemesUtils.manageThemesTextConfig;
  contentThemesConfig: ThemesTextEnum;
  customClassTitle: string;
  customClassSubtitle: string;
  customClassDescription: string;
  hasNoData = false;

  constructor(
    private cdRef: ChangeDetectorRef,
    private renderer: Renderer2
  ) {
    super();
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      if (this.events) {
        this.eventsSubscription = this.events.subscribe(() => {
          if (this.actionEl) {
            this.actionEl.nativeElement.click();
            this.cdRef.markForCheck();
          }
        });
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);

    if (!this.titleThemesConfig && this.parentsThemesConfig) {
      this.manageThemesTextConfig(this.parentsThemesConfig);
    }

    if (this.titleThemesConfig) {
      this.contentThemesConfig = this.titleThemesConfig;
    }

    this.manageSize();
    this.checkData();
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      /* A11Y */
      if (this.heading?.description && this.descriptionText) {
        this.descriptionLinks = this.descriptionText?.nativeElement.querySelectorAll('a');
        this.setLinksTabIndex('-1');
      }

      if (!this.titleThemesConfig && this.parentsThemesConfig) {
        this.manageThemesTextConfig(this.parentsThemesConfig);
      }

      if (this.titleThemesConfig) {
        this.contentThemesConfig = this.titleThemesConfig;
      }

      this.manageSize();
      this.checkData();
    }
  }

  ngOnDestroy() {
    this.eventsSubscription?.unsubscribe();
  }

  toggleCollapse(opened: boolean) {
    this.isOpen = opened;
    /* A11Y */
    if (this.isOpen) {
      this.setLinksTabIndex('0');
    } else {
      this.setLinksTabIndex('-1');
    }
  }

  manageThemesTextConfig(theme: ThemesEnum | BrandThemesEnum) {
    this.contentThemesConfig = this.manageThemesTextConfigUtils(theme);
  }

  checkData() {
    this.hasNoData =
      !this.heading?.title &&
      !this.heading?.subtitle &&
      !this.heading?.description;
    this.noData.emit(this.hasNoData);
  }

  manageSize() {
    if (this.size) {
      switch (this.size) {
        case SizesEnum.xl:
          this.customClassTitle = 'brand--h2';
          this.customClassSubtitle = 'is-body-xl';
          this.customClassDescription = 'is-body-m';
          break;
        case SizesEnum.l:
          this.customClassTitle = 'brand--h3';
          this.customClassSubtitle = 'brand--h5';
          this.customClassDescription = 'is-body-m';
          break;
        case SizesEnum.m:
          this.customClassTitle = 'brand--h4';
          this.customClassSubtitle = 'is-body-m is-font-default';
          this.customClassDescription = 'is-body-s';
          break;
        case SizesEnum.s:
          this.customClassTitle = 'brand--h5';
          this.customClassSubtitle = 'is-body-s is-font-default';
          this.customClassDescription = 'is-body-s';
          break;
        default:
          this.customClassTitle = 'brand--h3';
          this.customClassSubtitle = 'brand--h5';
          this.customClassDescription = 'is-body-m';
          break;
      }
    }
  }

  setLinksTabIndex(tabIndex: string) {
    if (this.descriptionLinks?.length > 0) {
      for (const descriptionLink of this.descriptionLinks) {
        this.renderer.setAttribute(descriptionLink, 'tabindex', tabIndex);
      }
    }
  }
}
